export type Dataset = {
  id: string;
  species: "T. b. brucei" | "T. cruzi";
  speciesFull: string;
  speciesZh: string;
  author: string;
  year: number;
  journal: string;
  cells: number;
  technology: string;
  cellBasisEn: string;
  cellBasisZh: string;
  scopeEn: string;
  scopeZh: string;
  stagesEn: string;
  stagesZh: string;
  doi: string;
  repository: string;
  repositoryUrl: string;
  processed: string;
  processedUrl: string;
  atlasUrl?: string;
  portalUrl?: string;
  cellBrowserUrl?: string;
  recordsUrl?: string;
  tier: "Core" | "Enhanced";
  recordStatusEn: string;
  recordStatusZh: string;
  sourceLayerEn: string;
  sourceLayerZh: string;
  portalOperationEn: string;
  portalOperationZh: string;
  dataLicenceEn: string;
  dataLicenceZh: string;
  lastVerified: string;
  sourceRegistryId: string;
  recordVersion: string;
  curationStatus:
    | "verified"
    | "verified-with-boundary"
    | "corrected-and-verified";
  availability:
    | "interactive-atlas"
    | "source-bounded-explorer"
    | "provenance-only";
  sourceLayer:
    | "author-hosted-atlas"
    | "author-deposited-matrix"
    | "author-deposited-technical-matrix"
    | "self-hosted-author-deposit";
  portalOperation:
    | "decode-only"
    | "static-conversion"
    | "sample-index"
    | "provenance-index";
  licenceStatus:
    | "verified"
    | "non-standard"
    | "not-recorded"
    | "not-independently-verified";
  licenceEvidenceUrl?: string;
  expressionUnitEn: string;
  expressionUnitZh: string;
  coordinateStatusEn: string;
  coordinateStatusZh: string;
  referenceEn: string;
  referenceZh: string;
  limitationEn: string;
  limitationZh: string;
};

export const datasets: Dataset[] = [
  {
    id: "brg-2021-bsf",
    species: "T. b. brucei",
    speciesFull: "Trypanosoma brucei brucei",
    speciesZh: "布氏布氏锥虫",
    author: "Briggs et al.",
    year: 2021,
    journal: "Nature Communications",
    cells: 10894,
    technology: "10x Chromium Single Cell 3′ v3",
    cellBasisEn:
      "Author-deposited filtered matrices: 8,599 WT + 2,295 ZC3H20-KO cells",
    cellBasisZh:
      "作者存档过滤矩阵：8,599个野生型 + 2,295个ZC3H20敲除细胞",
    scopeEn: "Bloodstream differentiation time course and ZC3H20 knockout",
    scopeZh: "体外血流型分化时间序列与ZC3H20敲除",
    stagesEn: "Slender · transitional · stumpy bloodstream forms",
    stagesZh: "细长型 · 过渡型 · 粗短型血流期",
    doi: "https://doi.org/10.1038/s41467-021-25607-2",
    repository: "ENA PRJEB41744",
    repositoryUrl: "https://www.ebi.ac.uk/ena/browser/view/PRJEB41744",
    processed: "Zenodo 5163554",
    processedUrl: "https://zenodo.org/records/5163554",
    atlasUrl:
      "https://cellatlas-cxg.mvls.gla.ac.uk/view/Tbrucei.h5ad/",
    portalUrl: "/explore/?species=tbb&view=gene&gene=Tb927.6.4280",
    recordsUrl: "/datasets/prjeb41744/",
    tier: "Core",
    recordStatusEn: "Verified",
    recordStatusZh: "已核实",
    sourceLayerEn:
      "Self-hosted sparse conversion of author-deposited filtered matrices and pinned author coordinates",
    sourceLayerZh: "本站自托管的作者过滤矩阵稀疏转换与固定作者坐标",
    portalOperationEn:
      "Versioned same-origin conversion with no author-service request; optional exploratory statistics are portal-derived and separately labelled",
    portalOperationZh:
      "版本化同域转换且不请求作者服务；可选探索性统计为本站派生并独立标注",
    dataLicenceEn:
      "Zenodo archive: CC BY 4.0; separate author-H5AD layer scope not yet documented",
    dataLicenceZh:
      "Zenodo存档：CC BY 4.0；独立作者H5AD数据层的许可范围尚未形成文件证据",
    lastVerified: "2026-07-29",
    sourceRegistryId: "prjeb41744",
    recordVersion: "2026.07.29.1",
    curationStatus: "verified",
    availability: "interactive-atlas",
    sourceLayer: "self-hosted-author-deposit",
    portalOperation: "static-conversion",
    licenceStatus: "not-independently-verified",
    licenceEvidenceUrl: "https://zenodo.org/records/5163554",
    expressionUnitEn: "Raw transcript counts",
    expressionUnitZh: "原始转录本计数",
    coordinateStatusEn: "Author UMAP and PCA available",
    coordinateStatusZh: "作者UMAP与PCA均可用",
    referenceEn:
      "TREU927, TriTrypDB release 50; Lister 427 maxi-circle M94286.1",
    referenceZh:
      "TREU927（TriTrypDB release 50）；Lister 427大环DNA M94286.1",
    limitationEn:
      "ENA exposed submitted BAM/BAI files but no FASTQ or SRA FTP links at audit time. The Zenodo licence is verified for its deposit, not yet for the separate author-H5AD-derived coordinates and metadata.",
    limitationZh:
      "核验时ENA提供提交的BAM/BAI文件，但未提供FASTQ或SRA FTP链接。Zenodo许可已核实适用于其存档，但尚无证据证明其覆盖独立作者H5AD来源的坐标与元数据。",
  },
  {
    id: "how-2022-tsetse",
    species: "T. b. brucei",
    speciesFull: "Trypanosoma brucei brucei",
    speciesZh: "布氏布氏锥虫",
    author: "Howick et al.",
    year: 2022,
    journal: "PLOS Pathogens",
    cells: 388,
    technology: "Modified Smart-seq2",
    cellBasisEn:
      "Fly-derived parasite transcriptomes; 46 in-vitro procyclic validation cells are not included",
    cellBasisZh:
      "采采蝇来源寄生虫转录组；不含用于方法验证的46个体外前循环型细胞",
    scopeEn: "Tsetse midgut, proventriculus and salivary gland",
    scopeZh: "采采蝇中肠、前胃与唾液腺发育阶段",
    stagesEn: "Procyclic · epimastigote · sexual · metacyclic forms",
    stagesZh: "前循环型 · 上鞭毛体 · 有性阶段 · 后循环型",
    doi: "https://doi.org/10.1371/journal.ppat.1010346",
    repository: "ENA ERP132258",
    repositoryUrl: "https://www.ebi.ac.uk/ena/browser/view/ERP132258",
    processed: "Zenodo 6047732",
    processedUrl: "https://zenodo.org/records/6047732",
    portalUrl: "/explore/?species=tbb-howick",
    cellBrowserUrl: "/cellbrowser/?ds=howick-2022-tsetse",
    recordsUrl: "/datasets/erp132258/",
    tier: "Core",
    recordStatusEn: "Verified with counting boundary",
    recordStatusZh: "已核实，附统计边界",
    sourceLayerEn: "Author raw-count matrix, metadata and UMAP",
    sourceLayerZh: "作者原始计数矩阵、元数据与UMAP",
    portalOperationEn:
      "Static browser conversion; optional exploratory statistics are portal-derived and separately labelled",
    portalOperationZh:
      "静态浏览格式转换；可选探索性统计为本站派生并独立标注",
    dataLicenceEn: "Zenodo records “other-open”; verify reuse terms",
    dataLicenceZh: "Zenodo标记为“other-open”；复用前需核对条款",
    lastVerified: "2026-07-27",
    sourceRegistryId: "erp132258",
    recordVersion: "2026.07.29.1",
    curationStatus: "verified-with-boundary",
    availability: "interactive-atlas",
    sourceLayer: "author-deposited-matrix",
    portalOperation: "static-conversion",
    licenceStatus: "non-standard",
    licenceEvidenceUrl: "https://zenodo.org/records/6047732",
    expressionUnitEn: "Smart-seq2 integer read counts; not UMI counts",
    expressionUnitZh: "Smart-seq2整数读段计数；并非UMI计数",
    coordinateStatusEn: "Author fig1 UMAP available for all 388 cells",
    coordinateStatusZh: "388个细胞均有作者fig1 UMAP坐标",
    referenceEn:
      "T. b. brucei 927 genome; HISAT2 2.1.0 and HTSeq 0.7.1",
    referenceZh:
      "T. b. brucei 927基因组；HISAT2 2.1.0与HTSeq 0.7.1",
    limitationEn:
      "DP is a sorting group, not a strain. C5 is mainly J10 and C6 mainly 1738, so stage and strain effects may be confounded; 387 of 388 cells match current ENA aliases.",
    limitationZh:
      "DP是分选组而非菌株。C5以J10为主、C6以1738为主，阶段与菌株效应可能混杂；388个细胞中有387个匹配当前ENA别名。",
  },
  {
    id: "brg-2023-replicative",
    species: "T. b. brucei",
    speciesFull: "Trypanosoma brucei brucei",
    speciesZh: "布氏布氏锥虫",
    author: "Briggs et al.",
    year: 2023,
    journal: "eLife",
    cells: 12006,
    technology: "10x Chromium Single Cell 3′ v3.1",
    cellBasisEn:
      "Author phase tables: 4,366 bloodstream-form + 7,640 procyclic-form cells",
    cellBasisZh:
      "作者阶段表：4,366个血流型 + 7,640个前循环型细胞",
    scopeEn: "Replicative bloodstream and procyclic forms",
    scopeZh: "复制型血流期与前循环期比较",
    stagesEn: "Bloodstream form · procyclic form · cell cycle",
    stagesZh: "血流型 · 前循环型 · 细胞周期",
    doi: "https://doi.org/10.7554/eLife.86325",
    repository: "ENA PRJEB58781",
    repositoryUrl: "https://www.ebi.ac.uk/ena/browser/view/PRJEB58781",
    processed: "Zenodo 7508131",
    processedUrl: "https://zenodo.org/records/7508131",
    atlasUrl:
      "https://cellatlas-cxg.mvls.gla.ac.uk/view/bsf.h5ad/",
    portalUrl: "/explore/?species=tbb-cellcycle-bsf",
    recordsUrl: "/datasets/prjeb58781/",
    tier: "Core",
    recordStatusEn: "Verified",
    recordStatusZh: "已核实",
    sourceLayerEn: "Self-hosted signed author-deposited values and embeddings",
    sourceLayerZh: "本站自托管的有符号作者存档值与降维坐标",
    portalOperationEn: "Format conversion only; BSF and PCF remain separate",
    portalOperationZh: "仅格式转换；BSF与PCF保持分离",
    dataLicenceEn: "Zenodo record: CC BY 4.0",
    dataLicenceZh: "Zenodo记录：CC BY 4.0",
    lastVerified: "2026-07-29",
    sourceRegistryId: "prjeb58781",
    recordVersion: "2026.07.29.1",
    curationStatus: "verified",
    availability: "interactive-atlas",
    sourceLayer: "self-hosted-author-deposit",
    portalOperation: "static-conversion",
    licenceStatus: "verified",
    licenceEvidenceUrl: "https://zenodo.org/records/7508131",
    expressionUnitEn:
      "Signed values from author-deposited BSF_normcounts.csv / PCF_normcounts.csv variable-feature subsets; not raw UMI counts",
    expressionUnitZh:
      "作者存档BSF_normcounts.csv / PCF_normcounts.csv变量特征子集中的有符号数值；并非原始UMI计数",
    coordinateStatusEn: "Separate author UMAP and MNN spaces for BSF and PCF",
    coordinateStatusZh: "BSF与PCF使用相互独立的作者UMAP和MNN空间",
    referenceEn:
      "Author mapping: T. brucei WT427 2018 with 3′ UTR +2,500 bp; L. major Friedlin used to remove multiplets; Cell Ranger 7",
    referenceZh:
      "作者比对参考：T. brucei WT427 2018，3′ UTR延长2,500 bp；同时比对L. major Friedlin以排除multiplet；Cell Ranger 7",
    limitationEn:
      "BSF and PCF embeddings were calculated separately and cannot be overlaid as one coordinate space.",
    limitationZh:
      "BSF与PCF降维坐标分别计算，不能叠加为同一坐标空间。",
  },
  {
    id: "reu-2023-skin",
    species: "T. b. brucei",
    speciesFull: "Trypanosoma brucei brucei",
    speciesZh: "布氏布氏锥虫",
    author: "Reuter et al.",
    year: 2023,
    journal: "Nature Communications",
    cells: 170,
    technology: "SMART-Seq v4 + Nextera XT",
    cellBasisEn:
      "Parasite transcriptomes passing QC: 44 MCF, 95 skin time-course and 31 BSF",
    cellBasisZh:
      "通过质控的寄生虫转录组：44个后循环型、95个皮肤时间序列和31个血流型",
    scopeEn: "Early infection in a human skin model",
    scopeZh: "人工人皮肤模型中的早期感染与组织适应",
    stagesEn: "Metacyclic · early skin · bloodstream forms",
    stagesZh: "后循环型 · 早期皮肤组织型 · 血流型",
    doi: "https://doi.org/10.1038/s41467-023-43437-2",
    repository: "GEO GSE174198",
    repositoryUrl: "https://www.ncbi.nlm.nih.gov/geo/query/acc.cgi?acc=GSE174198",
    processed: "GEO supplementary files",
    processedUrl: "https://www.ncbi.nlm.nih.gov/geo/query/acc.cgi?acc=GSE174198",
    portalUrl: "/datasets/gse174198/?gene=Tb927.6.4280",
    recordsUrl: "/datasets/gse174198/",
    tier: "Enhanced",
    recordStatusEn: "Corrected and verified with embedding boundary",
    recordStatusZh: "已纠正并核实，附降维边界",
    sourceLayerEn: "Author-deposited GEO expression tables",
    sourceLayerZh: "作者存档的GEO表达表",
    portalOperationEn: "Selectable sample index; no reconstructed PCA",
    portalOperationZh: "可选样本索引；未重建PCA",
    dataLicenceEn: "No separate data licence recorded in the GEO audit",
    dataLicenceZh: "GEO审核中未记录独立数据许可",
    lastVerified: "2026-07-27",
    sourceRegistryId: "gse174198",
    recordVersion: "2026.07.29.1",
    curationStatus: "corrected-and-verified",
    availability: "source-bounded-explorer",
    sourceLayer: "author-deposited-matrix",
    portalOperation: "sample-index",
    licenceStatus: "not-recorded",
    expressionUnitEn:
      "Normalized fractional skin values and separate integer BSF counts",
    expressionUnitZh: "皮肤样本归一化小数值与独立的BSF整数计数",
    coordinateStatusEn:
      "Author PCA reported in paper but reusable coordinates not deposited",
    coordinateStatusZh: "论文报告作者PCA，但未存档可复用坐标",
    referenceEn: "TREU927 release 48 plus ERCC sequences",
    referenceZh: "TREU927 release 48与ERCC序列",
    limitationEn:
      "The two expression tables have different units; the portal does not merge them or reconstruct PCA.",
    limitationZh:
      "两张表达表的单位不同；本站不合并矩阵，也不重建PCA。",
  },
  {
    id: "lai-2026-cruzi",
    species: "T. cruzi",
    speciesFull: "Trypanosoma cruzi",
    speciesZh: "克氏锥虫",
    author: "Laidlaw et al.",
    year: 2026,
    journal: "Nature Communications",
    cells: 31065,
    technology: "10x Chromium Next GEM 3′ v3.1",
    cellBasisEn: "Valid cells retained after author quality control",
    cellBasisZh: "经作者质量控制后保留的有效细胞",
    scopeEn: "In-vitro lifecycle atlas of the Silvio X10/7 A1 strain",
    scopeZh: "Silvio X10/7 A1株体外生命周期单细胞图谱",
    stagesEn: "Epimastigote · metacyclic · trypomastigote · amastigote",
    stagesZh: "上鞭毛体 · 后循环型 · 锥鞭毛体 · 无鞭毛体",
    doi: "https://doi.org/10.1038/s41467-026-73098-w",
    repository: "BioStudies E-MTAB-14406 · ENA ERP163729",
    repositoryUrl:
      "https://www.ebi.ac.uk/biostudies/arrayexpress/studies/E-MTAB-14406",
    processed: "Author H5AD atlas · active scaled matrix",
    processedUrl:
      "https://cellatlas-cxg.mvls.gla.ac.uk/view/Trypanosoma_cruzi_inVitro_cellatlas.h5ad/",
    atlasUrl:
      "https://cellatlas-cxg.mvls.gla.ac.uk/view/Trypanosoma_cruzi_inVitro_cellatlas.h5ad/",
    portalUrl: "/explore/?species=tc&view=gene&gene=C4B63_34g140",
    recordsUrl: "/datasets/e-mtab-14406/",
    tier: "Core",
    recordStatusEn: "Verified with matrix boundary",
    recordStatusZh: "已核实，附矩阵边界",
    sourceLayerEn: "Author-hosted processed CELL×GENE atlas",
    sourceLayerZh: "作者托管的处理后CELL×GENE图谱",
    portalOperationEn: "Decode active scaled matrix; no reanalysis",
    portalOperationZh: "解码活动缩放矩阵；未重新分析",
    dataLicenceEn: "Data-specific licence not yet independently verified",
    dataLicenceZh: "数据专用许可尚未独立核实",
    lastVerified: "2026-07-27",
    sourceRegistryId: "e-mtab-14406",
    recordVersion: "2026.07.29.1",
    curationStatus: "verified-with-boundary",
    availability: "interactive-atlas",
    sourceLayer: "author-hosted-atlas",
    portalOperation: "decode-only",
    licenceStatus: "not-independently-verified",
    expressionUnitEn:
      "Signed scaled values from the active author H5AD matrix; not raw counts",
    expressionUnitZh: "作者H5AD活动矩阵中的有符号缩放值；并非原始计数",
    coordinateStatusEn: "Author UMAP and PCA available",
    coordinateStatusZh: "作者UMAP与PCA均可用",
    referenceEn:
      "Author mapping: Dm28c 2018 plus maxicircle and 3′ UTR +2,500 bp; GRCh38 added only for AMA/EP/MIX. Related UTR asset: TriTrypDB release 68",
    referenceZh:
      "作者比对参考：Dm28c 2018、大环DNA及3′ UTR延长2,500 bp；仅AMA/EP/MIX另加入GRCh38。相关UTR资产来自TriTrypDB release 68",
    limitationEn:
      "BioStudies exposes IDF/SDRF and raw-read links but no processed count matrix.",
    limitationZh:
      "BioStudies提供IDF/SDRF与原始读段链接，但没有处理后计数矩阵。",
  },
  {
    id: "inc-2026-cruzi",
    species: "T. cruzi",
    speciesFull: "Trypanosoma cruzi",
    speciesZh: "克氏锥虫",
    author: "Inchausti et al.",
    year: 2026,
    journal: "eLife",
    cells: 3151,
    technology: "10x Chromium Next GEM 3′ v3.1",
    cellBasisEn:
      "Final retained singlets: 3,151; derived from 3,192 post-QC cells after removal of 41 doublets",
    cellBasisZh:
      "最终保留3,151个singlet；由3,192个质控后细胞移除41个双细胞得到",
    scopeEn:
      "Dm28c mammalian stages from infected rat H9c2 myoblasts and transitional cells",
    scopeZh:
      "感染大鼠H9c2成肌细胞获得的Dm28c哺乳动物期及过渡细胞",
    stagesEn: "Amastigote · transitional · cell-derived trypomastigote",
    stagesZh: "无鞭毛体 · 过渡细胞 · 细胞来源锥鞭毛体",
    doi: "https://doi.org/10.7554/eLife.105822",
    repository: "SRA PRJNA1200704",
    repositoryUrl: "https://www.ncbi.nlm.nih.gov/bioproject/PRJNA1200704",
    processed: "Zenodo 18760090 · 17 author-deposited files",
    processedUrl: "https://zenodo.org/records/18760090",
    recordsUrl: "/datasets/prjna1200704/",
    tier: "Core",
    recordStatusEn: "Verified with workflow discrepancy",
    recordStatusZh: "已核实，附流程差异",
    sourceLayerEn: "Author-deposited technical matrices and public code",
    sourceLayerZh: "作者存档的技术矩阵与公开代码",
    portalOperationEn: "Provenance index only; no reconstructed atlas",
    portalOperationZh: "仅建立溯源索引；未重建图谱",
    dataLicenceEn: "Zenodo record: CC BY 4.0",
    dataLicenceZh: "Zenodo记录：CC BY 4.0",
    lastVerified: "2026-07-27",
    sourceRegistryId: "prjna1200704",
    recordVersion: "2026.07.29.1",
    curationStatus: "verified-with-boundary",
    availability: "provenance-only",
    sourceLayer: "author-deposited-technical-matrix",
    portalOperation: "provenance-index",
    licenceStatus: "verified",
    licenceEvidenceUrl: "https://zenodo.org/records/18760090",
    expressionUnitEn:
      "Real-valued kallisto bustools expected counts in two technical matrices",
    expressionUnitZh: "两个技术矩阵中的kallisto bustools实数型期望计数",
    coordinateStatusEn:
      "Final author UMAP and post-doublet cell metadata not deposited",
    coordinateStatusZh: "作者最终UMAP与去除双细胞后的元数据未存档",
    referenceEn:
      "Dm28c 2018, TriTrypDB release 62, combined with Dm28c maxi-circle kDNA",
    referenceZh:
      "Dm28c 2018（TriTrypDB release 62）与Dm28c大环kDNA组合参考",
    limitationEn:
      "Final UMAP/cell metadata are not deposited; paper and public R script use different nUMI/nGene thresholds. SRA “Axenic culture” does not replace the infected-H9c2 sample origin.",
    limitationZh:
      "最终UMAP/细胞元数据未存档；论文与公开R脚本的nUMI/nGene阈值不同。SRA的“Axenic culture”字段不能替代感染H9c2的实验来源。",
  },
];

export const totalCells = datasets.reduce((sum, dataset) => sum + dataset.cells, 0);

export const gaps = [
  "T. b. gambiense",
  "T. b. rhodesiense",
  "T. congolense",
  "T. vivax",
  "T. evansi",
  "T. equiperdum",
  "T. rangeli",
];
